import React, { useRef } from 'react';
import { Box, TextField, Typography, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const HabilitacaoCreditoRetardatariaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Habilitação de Crédito Retardatária"
        customDescription="Pedido de habilitação de crédito após o prazo regular em procedimento de liquidação extrajudicial"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome do seu cliente: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: O autor é Igor Pereira Severino."
            value={values.nome_cliente || ''}
            onChange={(e) => handleFieldChange('nome_cliente', e.target.value)}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte"
            required
          />
        </Box>

        {/* 2. Fatos do Caso */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Quais são os fatos do caso? <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Ex: O autor, Igor Pereira Severino, é credor da instituição financeira XYZ S.A., atualmente em liquidação extrajudicial. O crédito decorre de aplicação financeira realizada em 15/03/2020, no valor de R$ 50.000,00. O autor não foi notificado do prazo para habilitação de crédito, tendo tomado conhecimento da liquidação apenas recentemente. Por esse motivo, requer a habilitação retardatária do crédito, conforme previsto na Lei 6.024/74."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 10000 }}
            required
          />
          <Typography
            variant="caption"
            color={(values.fatos_caso || '').length > 0 ? 'success.main' : 'text.secondary'}
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 10000 caracteres permitidos.
          </Typography>
        </Box>

        {/* 3. Nome da Parte Contrária */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Escreva o nome da parte contrária: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: A instituição em liquidação é o Banco XYZ S.A."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            required
          />
        </Box>

        {/* 4. Upload de Documentos (Opcional) */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Comprovantes do crédito, Extratos bancários, Contratos e Documentos de identificação."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os detalhes do caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="A habilitação retardatária é admitida na liquidação extrajudicial, mas pode implicar em perda de rateios já realizados. Justifique o atraso na habilitação e apresente documentos que comprovem a existência e o valor do crédito. O crédito será classificado conforme sua natureza (quirografário, privilegiado, etc.)." />
    </Box>
  );
};

export default HabilitacaoCreditoRetardatariaForm;

